import { FC, useState, useEffect } from 'react';
import { Table, TreeSelect, Button, Space, Tooltip, Empty } from 'antd';
import type { SortOrder } from 'antd/es/table/interface';
import {
	EditOutlined,
	SettingOutlined,
	DeleteOutlined,
} from '@ant-design/icons';
import { ConfirmDelete } from 'components/Global';
import SearchAndFilter from '../SearchAndFilter';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppDispatch } from 'redux/store';
import {
	getExpenseGroupsAction,
	deleteExpenseGroupAction,
	updateExpenseGroupAction,
} from 'redux/action/expenseGroupAction';
import { classOptionsAction } from 'redux/action/classAction';
import { chartOfAccountAction } from 'redux/action/chartOfAccountAction';
import { customerOptionsAction } from 'redux/action/customerAction';
import { locationOptionsAction } from 'redux/action/locationAction';
import { vendorAction } from 'redux/action/vendorAction';
import styles from './index.module.scss';
import './index.scss';
import { getOptionLabel } from 'utils/utils';
import ExpenseGroupSettingsModal from '../SettingsModal';
export interface ExpenseGroup {
	id: string;
	companyId: string;
	name: string;
	description?: string;
	accounts: string[];
	customers: string[];
	classes: string[];
	locations: string[];
	vendors?: string[];
	configAccounts: string[];
	createdAt: string;
	updatedAt: string;
	isUsed: boolean;
}

interface ExpenseGroupsTableProps {
	onEdit: (expenseGroup: ExpenseGroup) => void;
	closeDrawerByAnimation: () => void;
	isEditPermission: boolean;
	isDeletePermission: boolean;
}

// Helper function to transform Redux data to TreeSelect format (recursive for nested children)
const transformToTreeData = (data: any[]): any[] => {
	if (!Array.isArray(data)) {
		return [];
	}

	return data
		.filter((item: any) => item && item.Id)
		.map((item: any): any => ({
			title:
				item.title || item.Name || item.DisplayName || item.FullyQualifiedName,
			value: item.Id,
			key: item.Id,
			disabled: item.disabled === true,
			children: item.children && Array.isArray(item.children) && item.children.length > 0
				? transformToTreeData(item.children)
				: [],
		}));
};

const ExpenseGroupsTable: FC<ExpenseGroupsTableProps> = ({
	onEdit,
	closeDrawerByAnimation,
	isEditPermission,
	isDeletePermission,
}) => {
	const dispatch = useDispatch<AppDispatch>();
	const navigate = useNavigate();

	// Redux state
	const {
		data: expenseGroups,
		isLoading: tableLoading,
		fistTimeFetchLoading,
	} = useSelector((state: any) => state.expenseGroups);

	// Redux state for dropdown data
	const { optionData: qbClass } = useSelector((state: any) => state.class);
	const { data: qbChartOfAccounts } = useSelector(
		(state: any) => state.chartOfAccounts
	);
	const { optionData: qbCustomers } = useSelector(
		(state: any) => state.customer
	);
	const { optionData: qbLocations } = useSelector(
		(state: any) => state.locations
	);
	const { data: qbVendors } = useSelector((state: any) => state.vendors);

	// Redux state for allocation changes
	const { isLoading: allocationLoading } = useSelector(
		(state: any) => state.expenseGroupAllocation
	);

	const [accountsTooltipDisabled, setAccountsTooltipDisabled] = useState(false);
	const [customersTooltipDisabled, setCustomersTooltipDisabled] = useState(false);
	const [classesTooltipDisabled, setClassesTooltipDisabled] = useState(false);
	const [locationsTooltipDisabled, setLocationsTooltipDisabled] = useState(false);
	const [vendorsTooltipDisabled, setVendorsTooltipDisabled] = useState(false);

	// Track previous allocation loading state to detect completion
	const [prevAllocationLoading, setPrevAllocationLoading] =
		useState(allocationLoading);

	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [deleteLoading, setDeleteLoading] = useState(false);
	const [expenseGroupToDelete, setExpenseGroupToDelete] =
		useState<ExpenseGroup | null>(null);
	const [searchValue, setSearchValue] = useState('');
	const [isSettingsOpen, setIsSettingsOpen] = useState(false);
	const [selectedForSettings, setSelectedForSettings] =
		useState<ExpenseGroup | null>(null);

	// Determine if filters are active
	const hasActiveFilters = Boolean(searchValue?.trim());

	const CustomEmptyState = () => (
		<Empty
			image={Empty.PRESENTED_IMAGE_SIMPLE}
			description={
				(tableLoading || fistTimeFetchLoading)
					? null
					: hasActiveFilters
						? "No Expense Group records match the filter criteria"
						: "No Expense Group records are currently available"
			}
			style={{
				padding: '40px 20px',
				margin: '20px 0'
			}}
		/>
	);
	// Transform Redux data to TreeSelect format
	const treeData = {
		accounts: transformToTreeData(qbChartOfAccounts || []),
		vendors: transformToTreeData(qbVendors || []),
		customers: [
			{
				title: 'Blank Customer',
				value: '',
				key: 'blank',
				disabled: false,
				children: [],
			},
			...transformToTreeData(qbCustomers || []),
		],
		classes: [
			{
				title: 'Blank Class',
				value: '',
				key: 'blank',
				disabled: false,
				children: [],
			},
			...transformToTreeData(qbClass || []),
		],
		locations: [
			{
				title: 'Blank Location',
				value: '',
				key: 'blank',
				disabled: false,
				children: [],
			},
			...transformToTreeData(qbLocations || []),
		],
	};

	// Fetch expense groups and dropdown data on component mount
	useEffect(() => {
		dispatch(getExpenseGroupsAction({ page: 1, limit: 150 }));
		dispatch(classOptionsAction());
		dispatch(chartOfAccountAction());
		dispatch(customerOptionsAction());
		dispatch(locationOptionsAction());
		dispatch(vendorAction());
	}, [dispatch]);

	// Handle window resize to prevent table cutoff
	useEffect(() => {
		const handleResize = () => {
			// Force table to recalculate dimensions
			const tableElement = document.querySelector(
				'.ant-table-container'
			) as HTMLElement;
			if (tableElement) {
				// Trigger a reflow to force recalculation
				tableElement.style.width = '100%';
				tableElement.style.minWidth = '0';
			}
		};

		// Debounce resize events
		let timeoutId: NodeJS.Timeout;
		const debouncedResize = () => {
			clearTimeout(timeoutId);
			timeoutId = setTimeout(handleResize, 100);
		};

		window.addEventListener('resize', debouncedResize);
		return () => {
			window.removeEventListener('resize', debouncedResize);
			clearTimeout(timeoutId);
		};
	}, []);

	// Search functionality - call API with search parameters
	useEffect(() => {
		const timeoutId = setTimeout(() => {
			dispatch(
				getExpenseGroupsAction({
					page: 1,
					limit: 150,
					search: searchValue.trimStart() || undefined,
				})
			);
		}, 500); // Debounce search

		return () => clearTimeout(timeoutId);
	}, [searchValue, dispatch]);

	// Watch for allocation changes and refresh expense groups data
	useEffect(() => {
		// When allocation loading changes from true to false (allocation operation completed)
		if (prevAllocationLoading && !allocationLoading) {
			// Refresh expense groups data to reflect any changes
			dispatch(
				getExpenseGroupsAction({
					page: 1,
					limit: 150,
					search: searchValue.trimStart() || undefined,
				})
			);
		}
		// Update previous loading state
		setPrevAllocationLoading(allocationLoading);
	}, [allocationLoading, dispatch, searchValue, prevAllocationLoading]);

	// Search handler
	const performSearchHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSearchValue(event.target.value.trimStart());
	};

	const handleTreeSelectChange = async (
		value: string[],
		expenseGroupId: string,
		field: 'accounts' | 'customers' | 'classes' | 'locations'
	) => {
		// Special validation for accounts field
		// if (field === 'accounts') {
		// 	// Find the current expense group to get its current accounts
		// 	const currentExpenseGroup = expenseGroups?.find(
		// 		(group: ExpenseGroup) => group.id === expenseGroupId
		// 	);

		// 	// If there's only one account currently selected and user tries to deselect it
		// 	if (
		// 		currentExpenseGroup &&
		// 		currentExpenseGroup.accounts.length === 1 &&
		// 		value.length === 0
		// 	) {
		// 		toastText('At least one account must be selected.', 'error');
		// 		return;
		// 	}
		// }

		// Transform empty strings to 'blank' for customers, classes, and locations (similar to add/edit modal)
		let transformedValue = value;
		if (field === 'customers' || field === 'classes' || field === 'locations') {
			transformedValue = value.map((v) => (v === '' ? 'blank' : v));
		}

		// Call PUT API for real-time update
		dispatch(
			updateExpenseGroupAction({
				id: expenseGroupId,
				[field]: transformedValue,
			})
		);
	};

	const handleVendorsChange = (expenseGroupId: string, value: string[]) => {
		const transformedVendors = value.map((v) => (v === '' ? 'blank' : v));
		dispatch(
			updateExpenseGroupAction({
				id: expenseGroupId,
				vendors: transformedVendors,
			})
		);
	};

	const handleDelete = (expenseGroup: ExpenseGroup) => {
		setExpenseGroupToDelete(expenseGroup);
		setIsDeleteModalOpen(true);
	};

	const confirmDelete = async () => {
		if (!expenseGroupToDelete) return;

		setDeleteLoading(true);
		dispatch(deleteExpenseGroupAction(expenseGroupToDelete.id));
		setIsDeleteModalOpen(false);
		setExpenseGroupToDelete(null);
		setDeleteLoading(false);
	};

	const cancelDelete = () => {
		setIsDeleteModalOpen(false);
		setExpenseGroupToDelete(null);
	};
	const openSettings = (expenseGroup: ExpenseGroup) => {
		setSelectedForSettings(expenseGroup);
		setIsSettingsOpen(true);
	};

	const closeSettings = () => {
		setIsSettingsOpen(false);
		setSelectedForSettings(null);
	};
	// Handle deep linking to expense transactions
	const handleExpenseGroupClick = (expenseGroup: ExpenseGroup) => {
		// Close the settings drawer first
		closeDrawerByAnimation();
		// Small delay to ensure smooth drawer closing animation
		setTimeout(() => {
			// Navigate to expense allocations page with the selected expense group and force first tab
			navigate(`/expense-allocations?expenseGroup=${expenseGroup.id}&tab=1`);
		}, 100);
	};

	// Helper function to render TreeSelect with custom display
	const renderTreeSelect = (
		values: string[],
		record: ExpenseGroup,
		field: 'accounts' | 'customers' | 'classes' | 'locations',
		treeData: any[]
	) => {
		const fieldName = field.charAt(0).toUpperCase() + field.slice(1);

		// Transform 'blank' values back to empty strings for display (similar to add/edit modal)
		let displayValues = values;
		if (field === 'customers' || field === 'classes' || field === 'locations') {
			displayValues = values.map((v) => (v === 'blank' ? '' : v));
		}

		return (
			<div className={styles.treeSelectCell}>
				<TreeSelect
					multiple
					showSearch
					treeDefaultExpandAll
					value={displayValues}
					treeData={treeData}
					placeholder={`Select ${field}`}
					onChange={(value) => {
						if (isEditPermission) {
							handleTreeSelectChange(value, record.id, field);
						}
					}}
					disabled={!isEditPermission}
					style={{ minWidth: 150, height: 40, fontSize: '12px' }}
					dropdownStyle={{ minWidth: 350, maxHeight: 400, overflow: 'auto' }}
					size="large"
					treeLine
					filterTreeNode={(inputValue, treeNode) =>
						treeNode?.props?.title
							?.toLowerCase()
							.includes(inputValue.toLowerCase())
					}
					maxTagCount={0}
					onDropdownVisibleChange={(open) => {
						switch (field) {
							case 'accounts':
								setAccountsTooltipDisabled(open);
								break;
							case 'customers':
								setCustomersTooltipDisabled(open);
								break;
							case 'classes':
								setClassesTooltipDisabled(open);
								break;
							case 'locations':
								setLocationsTooltipDisabled(open);
								break;
						}
					}}
					maxTagPlaceholder={() => {
						if (displayValues.length === 0) return fieldName;
						const labels = displayValues.map((v) => {
							if (field === 'locations' && v === '') {
								return 'Blank Location';
							}
							return getOptionLabel(v, treeData);
						});
						return (
							<Tooltip
								title={
									<div className="filter-tooltip-content">
										{labels.map((l, i) => (
											<div
												key={i}
												className="filter-tooltip-item"
												style={{
													borderBottom:
														i !== labels.length - 1
															? '1px solid rgba(52, 71, 53, 0.1)'
															: 'none',
												}}
											>
												{l}
											</div>
										))}
									</div>
								}
								placement="bottom"
								overlayClassName="filter-tooltip"
								visible={
									(field === 'accounts' ? accountsTooltipDisabled :
										field === 'customers' ? customersTooltipDisabled :
											field === 'classes' ? classesTooltipDisabled :
												locationsTooltipDisabled) ? false : undefined
								}
								trigger={
									(field === 'accounts' ? accountsTooltipDisabled :
										field === 'customers' ? customersTooltipDisabled :
											field === 'classes' ? classesTooltipDisabled :
												locationsTooltipDisabled) ? [] : ['hover']
								}
							>
								{fieldName} ({displayValues.length})
							</Tooltip>
						);
					}}
					className={styles.customTreeSelect}
				/>
			</div>
		);
	};

	// Render Vendors column (TreeSelect, same pattern as other entity columns)
	const renderVendorSelect = (values: string[] | undefined, record: ExpenseGroup) => {
		const displayValues = (values || []).map((v) => (v === 'blank' ? '' : v));
		return (
			<div className={styles.treeSelectCell}>
				<TreeSelect
					multiple
					showSearch
					treeDefaultExpandAll
					value={displayValues}
					treeData={treeData.vendors}
					placeholder="Select vendors"
					onChange={(value) => {
						if (isEditPermission) {
							handleVendorsChange(record.id, value);
						}
					}}
					disabled={!isEditPermission}
					style={{ minWidth: 150, height: 40, fontSize: '12px' }}
					dropdownStyle={{ minWidth: 350, maxHeight: 400, overflow: 'auto' }}
					size="large"
					treeLine
					filterTreeNode={(inputValue, treeNode) =>
						treeNode?.props?.title
							?.toLowerCase()
							.includes(inputValue.toLowerCase())
					}
					maxTagCount={0}
					onDropdownVisibleChange={(open) => setVendorsTooltipDisabled(open)}
					maxTagPlaceholder={() => {
						if (displayValues.length === 0) return 'Vendors';
						const labels = displayValues.map((v) =>
							getOptionLabel(v, treeData.vendors)
						);
						return (
							<Tooltip
								title={
									<div className="filter-tooltip-content">
										{labels.map((l, i) => (
											<div
												key={i}
												className="filter-tooltip-item"
												style={{
													borderBottom:
														i !== labels.length - 1
															? '1px solid rgba(52, 71, 53, 0.1)'
															: 'none',
												}}
											>
												{l}
											</div>
										))}
									</div>
								}
								placement="bottom"
								overlayClassName="filter-tooltip"
								visible={vendorsTooltipDisabled ? false : undefined}
								trigger={vendorsTooltipDisabled ? [] : ['hover']}
							>
								Vendors ({displayValues.length})
							</Tooltip>
						);
					}}
					className={styles.customTreeSelect}
				/>
			</div>
		);
	};

	const columns = [
		{
			title: '#',
			dataIndex: 'srNo',
			key: 'srNo',
			width: '4%',
			align: 'left' as const,
			render: (_: any, __: any, index: number) => index + 1,
		},
		{
			title: 'Name',
			dataIndex: 'name',
			key: 'name',
			width: '18%',
			align: 'left' as const,
			sorter: (a: ExpenseGroup, b: ExpenseGroup) =>
				a.name.localeCompare(b.name),
			sortDirections: ['ascend', 'descend'] as SortOrder[],
			render: (name: string, record: ExpenseGroup) => (
				<div
					className={`${styles.nameCell} ${styles.clickableName}`}
					onClick={() => handleExpenseGroupClick(record)}
					style={{
						cursor: 'pointer',
					}}
				>
					<span
						style={{
							minWidth: 0,
							display: '-webkit-box',
							WebkitLineClamp: 2,
							WebkitBoxOrient: 'vertical',
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							lineHeight: '1.2',
							fontWeight: '500',
							color: '#1890ff',
						}}
						title={name}
					>
						{name}
					</span>
				</div>
			),
		},
		{
			title: 'Description',
			dataIndex: 'description',
			key: 'description',
			width: '16%',
			align: 'left' as const,
			render: (description: string) => (
				<div className={styles.descriptionCell} title={description}>
					{description}
				</div>
			),
		},
		{
			title: 'Accounts',
			dataIndex: 'accounts',
			key: 'accounts',
			width: '14%',
			align: 'left' as const,
			render: (accounts: string[], record: ExpenseGroup) =>
				renderTreeSelect(accounts, record, 'accounts', treeData.accounts),
		},
		{
			title: 'Customers',
			dataIndex: 'customers',
			key: 'customers',
			width: '14%',
			align: 'left' as const,
			render: (customers: string[], record: ExpenseGroup) =>
				renderTreeSelect(customers, record, 'customers', treeData.customers),
		},
		{
			title: 'Classes',
			dataIndex: 'classes',
			key: 'classes',
			width: '14%',
			align: 'left' as const,
			render: (classes: string[], record: ExpenseGroup) =>
				renderTreeSelect(classes, record, 'classes', treeData.classes),
		},
		{
			title: 'Locations',
			dataIndex: 'locations',
			key: 'locations',
			width: '14%',
			align: 'left' as const,
			render: (locations: string[], record: ExpenseGroup) =>
				renderTreeSelect(locations, record, 'locations', treeData.locations),
		},
		{
			title: 'Vendors',
			dataIndex: 'vendors',
			key: 'vendors',
			width: '14%',
			align: 'left' as const,
			render: (vendors: string[] | undefined, record: ExpenseGroup) =>
				renderVendorSelect(vendors, record),
		},
		{
			title: 'Actions',
			key: 'actions',
			width: '10%',
			align: 'center' as const,
			fixed: 'right' as const,
			render: (_: any, record: ExpenseGroup) => (
				<Space size={8}>
					{isEditPermission && (
						<>
							<Button
								type="text"
								icon={<EditOutlined />}
								onClick={() => onEdit(record)}
								title="Edit"
								className={styles.actionButton}
								size="small"
							/>
							<Button
								type="text"
								icon={<SettingOutlined />}
								title="Settings"
								className={styles.actionButton}
								size="small"
								onClick={() => openSettings(record)}
							/>
						</>
					)}
					{isDeletePermission && (
						<Button
							type="text"
							icon={<DeleteOutlined />}
							onClick={() => handleDelete(record)}
							title="Delete"
							className={styles.actionButton}
							size="small"
							danger
						/>
					)}
				</Space>
			),
		},
	];

	return (
		<>
			<div className={'expense-groups-table'}>
				<SearchAndFilter
					performSearchHandler={performSearchHandler}
					searchValue={searchValue}
				/>
				<Table
					columns={columns}
					dataSource={expenseGroups || []}
					rowKey="id"
					className="table-global"
					pagination={false}
					scroll={{ y: '59vh', x: '100%' }}
					size="small"
					loading={tableLoading || fistTimeFetchLoading}
					locale={{ emptyText: <CustomEmptyState /> }}
				/>
			</div>

			<ConfirmDelete
				isModalOpen={isDeleteModalOpen}
				handleOk={confirmDelete}
				handleCancel={cancelDelete}
				deleteHandler={confirmDelete}
				isLoading={deleteLoading}
				deleteMessage={`delete "${expenseGroupToDelete?.name}"`}
			/>
			{isSettingsOpen && (
				<ExpenseGroupSettingsModal
					isModalOpen={isSettingsOpen}
					handleCancel={closeSettings}
					expenseGroup={selectedForSettings}
				/>
			)}
		</>
	);
};

export default ExpenseGroupsTable;
